import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import { GAME_CATEGORY } from '@opm/components/FilterCommon/constants/games';
import MarketContentBody from '@opm/components/MarketContentBody';
import ScrollBottomList from '@opm/components/ScrollBottomList';
import useIntls from '@opm/hooks/useIntls';
import { TranslateConfigProvider } from '@opm/hooks/useTranslateConfig';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import {
  MahjongTopCreativesPoint,
  ArrowsTopCreativesPoint,
  Merge2TopCreativesPoint,
  Merge3TopCreativesPoint,
} from '@opm/operationLog/pages';
import { DBO_TM } from '@op/utils/format';
import BubbleCommon from '@op/components/BubbleCommon';
import React, { useCallback, useMemo, useRef } from 'react';
import { Layout } from 'tea-component';
import { useLocation } from 'umi';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import { adCreativesHeaderTipsMap, adCreativesStopTipsMap } from '@opm/components/BubbleCommon';
import useMenuTitle from '@opm/hooks/useMenuTitle';
import CompetitorsAdCreativesTable from '../CompetitorsAdCreatives/CompetitorsAdCreativesTable';
import { adCreativesProcessor } from '../CompetitorsAdCreatives/processors';
import {
  mergeMarketingBuriedPointForStudio,
  type IStudioEmbedLogConfig,
} from '@opm/utils/studioEmbedLog';
import {
  getGameIdsByGenre,
  getZoneTypeFromPath,
  ZONE_CONFIG,
  type TZoneType,
} from './constants';
import marketConnectWithCond from '@opm/components/MarketConnect/MarketConnectWithCond';
import {
  dealwithMaterialsList,
  IMaterialsListDefaultFilter,
  MATERIALS_LIST_API,
} from '@opm/components/MarketConnect/utils/materialsList';
import './index.scss';

const { Content } = Layout;

const ZONE_TOP_CREATIVES_BURIED_POINT_MAP: Record<TZoneType, typeof MahjongTopCreativesPoint> = {
  MahjongSolitaire: MahjongTopCreativesPoint,
  Arrows: ArrowsTopCreativesPoint,
  Merge2: Merge2TopCreativesPoint,
  Merge3: Merge3TopCreativesPoint,
};

const fallbackDefaultFilter = {
  ...DBO_TM.filterRangeDay(6, 0),
  date_type: 'daily' as const,
};

export interface IZoneTopCreativesProps {
  defaultFilter?: IMaterialsListDefaultFilter;
  zoneGameIds?: string[];
  zoneType?: TZoneType;
  titleEn?: string;
  titleCn?: string;
  enableSourcePageLog?: boolean;
  hideGameCategoryTabs?: boolean;
  hidePageHeader?: boolean;
  /** 筛选器实例 ID，用于隔离 sessionStorage / Redux 筛选状态 */
  filterInstanceId?: string;
  /** Studio 嵌入时的 Tab logConfig，用于覆盖 FilterSubmit 等按钮打点 */
  studioLogConfig?: IStudioEmbedLogConfig;
  /**
   * Studio 嵌入时由 marketConnectWithCond 使用：
   * - 拉 cond 时作为 game_ids 入参
   * - 有值时直接作为 last_available_time / zoneGameIds 的游戏列表，跳过 genre 过滤
   */
  condGameIds?: string[];
}

function useZoneGameIdsResolver(props: IZoneTopCreativesProps) {
  const location = useLocation();
  const condGameIdsKey = useMemo(
    () => (props.condGameIds?.length ? [...props.condGameIds].sort().join(',') : ''),
    [props.condGameIds],
  );
  const genre = useMemo(() => {
    if (props.zoneType && ZONE_CONFIG[props.zoneType]) {
      return ZONE_CONFIG[props.zoneType].genre;
    }
    const zoneType = getZoneTypeFromPath(location.pathname);
    return zoneType ? ZONE_CONFIG[zoneType].genre : undefined;
  }, [location.pathname, props.zoneType]);

  return useCallback(
    (condData: Record<string, any>) => {
      if (props.condGameIds?.length) {
        return props.condGameIds;
      }
      if (!genre) return [];
      return getGameIdsByGenre(condData?.materials_config_games || [], genre);
    },
    [genre, condGameIdsKey],
  );
}

function ZoneTopCreatives({
  defaultFilter,
  zoneGameIds = [],
  zoneType: zoneTypeProp,
  titleEn,
  titleCn,
  enableSourcePageLog = true,
  hideGameCategoryTabs = false,
  hidePageHeader = false,
  filterInstanceId,
  studioLogConfig,
}: IZoneTopCreativesProps) {
  const { currentTitle } = useMenuTitle();
  const { intl, isEn } = useIntls();
  const location = useLocation();

  const zoneType = useMemo(() => {
    if (zoneTypeProp) return zoneTypeProp;
    return getZoneTypeFromPath(location.pathname);
  }, [zoneTypeProp, location.pathname]);

  const buriedPoint = useMemo(() => {
    const marketingBuriedPoint = zoneType
      ? ZONE_TOP_CREATIVES_BURIED_POINT_MAP[zoneType]
      : MahjongTopCreativesPoint;
    return mergeMarketingBuriedPointForStudio(marketingBuriedPoint, studioLogConfig);
  }, [zoneType, studioLogConfig]);
  DBOM_POINT.useInitPage({
    buriedPoint,
    triggerPagePoint: enableSourcePageLog,
  });
  const zoneConfig = zoneType ? ZONE_CONFIG[zoneType] : null;

  const zoneGameIdsRef = useRef<string[]>(zoneGameIds);
  zoneGameIdsRef.current = zoneGameIds;

  const mergedDefaultFilter = useMemo(
    () => ({
      ...fallbackDefaultFilter,
      ...(hideGameCategoryTabs ? { game_category: GAME_CATEGORY.ALL } : {}),
      ...defaultFilter,
      ...(hideGameCategoryTabs ? { game_category: GAME_CATEGORY.ALL } : {}),
    }),
    [defaultFilter, hideGameCategoryTabs],
  );

  const { filter, setFilter, loading, ...filterProps } = useFilterCommon({
    keys: [
      'ADGame',
      'ADMultiDateRange',
      'ADCountryRegion',
      'ADChannel',
      'ADType',
      'ADPlatform',
      'ADImpressions',
      'ADActiveDays',
      'ADFirstSeenDateSelect',
      'ADLastSeenDateSelect',
    ],
    instanceId: filterInstanceId,
  });

  const filterConfigProps = useMemo(() => {
    const adGameConfig: Record<string, unknown> = {};
    if (hideGameCategoryTabs) {
      adGameConfig.hideCategoryTabs = true;
    } else if (zoneConfig) {
      adGameConfig.filterGenre = zoneConfig.genre;
    }
    if (!Object.keys(adGameConfig).length) return undefined;
    return { ADGame: adGameConfig };
  }, [hideGameCategoryTabs, zoneConfig]);

  const finallyDealwithDefaultValue = useCallback((value: any) => {
    const nextValue = hideGameCategoryTabs
      ? { ...value, game_category: GAME_CATEGORY.ALL }
      : value;
    const gameIds = nextValue?.game_ids;
    if (!gameIds || gameIds.length === 0) {
      return { ...nextValue, game_ids: zoneGameIdsRef.current };
    }
    if (zoneConfig && !hideGameCategoryTabs) {
      const validIds = new Set(zoneGameIdsRef.current);
      const filtered = gameIds.filter((id: string) => validIds.has(id));
      return {
        ...nextValue,
        game_ids: filtered.length ? filtered : zoneGameIdsRef.current,
      };
    }
    return nextValue;
  }, [hideGameCategoryTabs, zoneConfig]);

  const handleConfirm = useCallback((value: any) => {
    const normalizedValue = hideGameCategoryTabs
      ? { ...value, game_category: GAME_CATEGORY.ALL }
      : value;
    const gameIds = normalizedValue.game_ids;
    const finalValue = (!gameIds || gameIds.length === 0)
      ? { ...normalizedValue, game_ids: zoneGameIdsRef.current }
      : normalizedValue;
    setFilter(finalValue);
  }, [hideGameCategoryTabs, setFilter]);

  let configuredTitle: string | undefined;
  if (titleEn && titleCn) {
    configuredTitle = isEn ? titleEn : titleCn;
  }
  let zoneTitle: string | undefined;
  if (zoneConfig) {
    zoneTitle = isEn ? zoneConfig.title : zoneConfig.titleCn;
  }
  const pageTitle = configuredTitle ?? zoneTitle ?? currentTitle;

  return (
    <TranslateConfigProvider processor={adCreativesProcessor}>
      <ScrollBottomList>
        <Content className="dbom-zone-top-creatives-page">
          {!hidePageHeader && (
            <Content.Header
              title={pageTitle}
              subtitle={
                <div className="dbo-header-subtitle">
                  {intl({ id: 'Data Update' })}
                  <BubbleCommon showIcon={false} tipsMap={adCreativesHeaderTipsMap}>
                    <TipContext className="title-tip" placement="bottom" />
                  </BubbleCommon>
                </div>
              }
            />
          )}
          <MarketContentBody
            loading={loading}
            horseRaceLampTips={adCreativesStopTipsMap}
            filterSlot={
              <FilterCommon
                {...filterProps}
                userDefaultValue={mergedDefaultFilter}
                finallyDealwithDefaultValue={finallyDealwithDefaultValue}
                loadingHeight={150}
                useOriDefaultValue
                realTimeMode
                filterConfigProps={filterConfigProps}
                confirm={handleConfirm}
              />
            }
          >
            <CompetitorsAdCreativesTable
              filter={filter}
              defaultView="card"
              verticalCard={true}
            />
          </MarketContentBody>
        </Content>
      </ScrollBottomList>
    </TranslateConfigProvider>
  );
}

export default marketConnectWithCond<IZoneTopCreativesProps>({
  ids: [MATERIALS_LIST_API],
  extrRequestKeys: ['materials_config_games'],
  useResolveGameIds: useZoneGameIdsResolver,
  dealwith: dealwithMaterialsList(MATERIALS_LIST_API),
})(ZoneTopCreatives);
